// Angular imports
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { EventEmitter, Injectable, Output } from '@angular/core';

// CC-Common imports
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Injectable({
  providedIn: 'root'
})
export class ScreenService {
  @Output() changed = new EventEmitter<void>();
  @Output() resized = new EventEmitter<void>();

  SCREEN_XS_PARAMS = {
    header: {
      logoLocation: 'center',
      menuButtonWidth: '60px'
    },
    userpanel: {
      menuButtonWidth: '170px'
    },
    home: {},
    help: {
      toolBarHeight: '60',
      menuDropDownWidth: '170px',
      menuCiraAppWidth: '230px',
      drawerOpenMode: true
    },
    searchpanel: {
      menuSearchWidth: '90%'
    },
    contentTree: {
      treeWidth: '350',
      searchPanelWidth: '340'
    },
    contextTree: {
      treeWidth: '350',
      searchPanelWidth: '340'
    },
    content: {
      maxWidthEmailHelpPopup: '100%',
      maxHeightEmailHelpPopup: '100%',
      maxWidthHelpURLPopup: '100%',
      maxHeightHelpURLPopup: '100%',
      maxWidthPrintPreviewPopup: '100%',
      maxHeightPrintPreviewPopup: '100%'
    }
  };

  SCREEN_SM_PARAMS = {
    header: {
      logoLocation: 'center',
      menuButtonWidth: '60px'
    },
    userpanel: {
      menuButtonWidth: '170px'
    },
    home: {},
    help: {
      toolBarHeight: '60',
      menuDropDownWidth: '170px',
      menuCiraAppWidth: '230px',
      drawerOpenMode: false
    },
    searchpanel: {
      menuSearchWidth: '400px'
    },
    contentTree: {
      treeWidth: '350',
      searchPanelWidth: '340'
    },
    contextTree: {
      treeWidth: '350',
      searchPanelWidth: '340'
    },
    content: {
      maxWidthEmailHelpPopup: '100%',
      maxHeightEmailHelpPopup: '100%',
      maxWidthHelpURLPopup: '100%',
      maxHeightHelpURLPopup: '100%',
      maxWidthPrintPreviewPopup: '100%',
      maxHeightPrintPreviewPopup: '100%'
    }
  };

  SCREEN_MD_PARAMS = {
    header: {
      logoLocation: 'before',
      menuButtonWidth: '60px'
    },
    userpanel: {
      menuButtonWidth: '170px'
    },
    home: {},
    help: {
      toolBarHeight: '60',
      menuDropDownWidth: '170px',
      menuCiraAppWidth: '230px',
      drawerOpenMode: true
    },
    searchpanel: {
      menuSearchWidth: '500px'
    },
    contentTree: {
      treeWidth: '350',
      searchPanelWidth: '340'
    },
    contextTree: {
      treeWidth: '350',
      searchPanelWidth: '340'
    },
    content: {
      maxWidthEmailHelpPopup: '100%',
      maxHeightEmailHelpPopup: '100%',
      maxWidthHelpURLPopup: '100%',
      maxHeightHelpURLPopup: '100%',
      maxWidthPrintPreviewPopup: '100%',
      maxHeightPrintPreviewPopup: '100%'
    }
  };

  SCREEN_LG_PARAMS = {
    header: {
      logoLocation: 'before',
      menuButtonWidth: '230px'
    },
    userpanel: {
      menuButtonWidth: '170px'
    },
    home: {},
    help: {
      toolBarHeight: '60',
      menuDropDownWidth: '170px',
      menuCiraAppWidth: '230px',
      drawerOpenMode: true
    },
    searchpanel: {
      menuSearchWidth: '600px'
    },
    contentTree: {
      treeWidth: '350',
      searchPanelWidth: '340'
    },
    contextTree: {
      treeWidth: '350',
      searchPanelWidth: '340'
    },
    content: {
      maxWidthEmailHelpPopup: '500px',
      maxHeightEmailHelpPopup: '250px',
      maxWidthHelpURLPopup: '500px',
      maxHeightHelpURLPopup: '230px',
      maxWidthPrintPreviewPopup: '700px',
      maxHeightPrintPreviewPopup: '800px'
    }
  };

  constructor(private breakpointObserver: BreakpointObserver) {
    RxjsUtils.subscribeToObservable(
      this.breakpointObserver.observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large
      ]),
      () => this.changed.next()
    );

    let timerId: any;
    window.addEventListener('resize', () => {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        this.resized.next();
        clearTimeout(timerId);
      }, 750);
    });
  }

  public isLargeScreen(): boolean {
    const isLarge = this.breakpointObserver.isMatched(Breakpoints.Large);
    const isXLarge = this.breakpointObserver.isMatched(Breakpoints.XLarge);

    return isLarge || isXLarge;
  }

  public isSmallScreen() {
    const isSmall =
      this.breakpointObserver.isMatched(Breakpoints.Small) ||
      this.breakpointObserver.isMatched(Breakpoints.XSmall);
    return isSmall;
  }

  public isXSmallScreen() {
    const isXSmall = this.breakpointObserver.isMatched(Breakpoints.XSmall);
    return isXSmall;
  }

  public isMediumScreen() {
    const isMedium = this.breakpointObserver.isMatched(Breakpoints.Medium);
    return isMedium;
  }

  public get sizes() {
    return {
      'screen-x-small': this.breakpointObserver.isMatched(Breakpoints.XSmall),
      'screen-small': this.breakpointObserver.isMatched(Breakpoints.Small),
      'screen-medium': this.breakpointObserver.isMatched(Breakpoints.Medium),
      'screen-large': this.isLargeScreen()
    };
  }

  public get getCurrentScreenSize(): string {
    if (this.breakpointObserver.isMatched(Breakpoints.XSmall)) {
      return 'screen-x-small';
    } else if (this.breakpointObserver.isMatched(Breakpoints.Small)) {
      return 'screen-small';
    } else if (this.breakpointObserver.isMatched(Breakpoints.Medium)) {
      return 'screen-medium';
    } else if (this.isLargeScreen()) {
      return 'screen-large';
    }
    // return large screen as default
    return 'lg';
  }

  public getScreenParams(size: string) {
    if (size.length > 1) {
      switch (size) {
        case 'screen-x-small': {
          return this.SCREEN_XS_PARAMS;
        }
        case 'screen-small': {
          return this.SCREEN_SM_PARAMS;
        }
        case 'screen-medium': {
          return this.SCREEN_MD_PARAMS;
        }
        case 'screen-large': {
          return this.SCREEN_LG_PARAMS;
        }
      }
    }

    //Return Small screen view by default
    return this.SCREEN_LG_PARAMS;
  }
}
